import React, {
    useEffect,
    useState,
    useMemo,
    useRef,
    useCallback,
} from 'react';
import { CardContent, CircularProgress, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { maisVendasApi } from '~/services/api';

// Util import
import SnackBar from '~/util/SnackBar';
import {
    getTotalValue,
    getListByFilter,
    applyOrdersFilter,
    applySearchFilter,
    applyBrandFilter,
    getCartsListByFilter,
} from '~/util/orderPanelFunctions';


// Local components import
import PanelTitle from './PanelTitle';
import SearchBar from './SearchBar';
import FilterButtons from './FilterButtons';
import PanelTotals from './PanelTotals';
import PanelList from './PanelList';

import * as Style from './styles';

const getClientDataKey = order => `${order.orderId}|${order.clientId}`;

function OrderListPanel({
    orders,
    brandFilter,
    setBrandFilter,
    multimarca,
    ordersLoaded,
    setCurrentPage,
    cartsLoaded,
}) {
    // Hooks
    const { isFaturado } = useSelector(state => state.status);
    const location = useLocation().pathname;

    // Local states
    const [filter, setFilter] = useState('Todos');
    const [ordersFilter, setOrdersFilter] = useState('allOrders');
    const [searchFilter, setSearchFilter] = useState('');
    const [clientDataByOrder, setClientDataByOrder] = useState({});
    const requestedClientData = useRef(new Set());

    const isAtMaisCarrinho = location === '/maisCarrinho';

    const loadClientData = useCallback(async visibleOrders => {
        const pendingOrders = new Map();
        visibleOrders.forEach(order => {
            const key = getClientDataKey(order);
            if (!requestedClientData.current.has(key)) {
                pendingOrders.set(key, order);
            }
        });

        if (!pendingOrders.size) return;

        pendingOrders.forEach((_, key) => requestedClientData.current.add(key));

        try {
            const response = await maisVendasApi.post(
                `salesperson/orders/client-data`,
                {
                    orderData: [...pendingOrders.values()].map(order => ({
                        orderId: order.orderId,
                        clientId: order.clientId,
                    })),
                }
            );

            if (!Array.isArray(response?.data)) {
                throw new Error('Resposta inválida do client-data');
            }

            setClientDataByOrder(current => {
                const next = { ...current };
                response.data.forEach(client => {
                    next[getClientDataKey(client)] = client.clientData;
                });
                return next;
            });
        } catch {
            pendingOrders.forEach((_, key) =>
                requestedClientData.current.delete(key)
            );
            SnackBar.error('Erro retornando dados do cliente');
        }
    }, []);

    const ordersWithClientData = useMemo(() => {
        if (!orders || isAtMaisCarrinho) return orders;

        return orders.map(order => {
            const clientData = clientDataByOrder[getClientDataKey(order)];
            return clientData ? { ...order, clientData } : order;
        });
    }, [orders, clientDataByOrder, isAtMaisCarrinho]);

    const filteredOrders = useMemo(() => {
        if (!ordersWithClientData) return [];

        if (isAtMaisCarrinho) {
            return getCartsListByFilter(ordersWithClientData, filter);
        }

        let list = ordersWithClientData;
        list = getListByFilter(list, filter);

        if (ordersFilter && ordersFilter !== 'allOrders') {
            list = applyOrdersFilter(list, ordersFilter);
        }

        if (searchFilter) {
            list = applySearchFilter(list, searchFilter);
        }

        list = applyBrandFilter(list, brandFilter);

        return list;
    }, [
        ordersWithClientData,
        filter,
        ordersFilter,
        searchFilter,
        brandFilter,
        isAtMaisCarrinho,
    ]);

    const totalFilterValue = useMemo(() => {
        if (isAtMaisCarrinho) return 'R$ 0,00';
        return getTotalValue(filteredOrders, ordersFilter);
    }, [filteredOrders, ordersFilter, isAtMaisCarrinho]);

    const hideOrdersFilter = useMemo(() => {
        return !['Todos', 'Vendas'].includes(filter);
    }, [filter]);

    const totalPanelCondition =
        [].includes(filter) &&
        ['allBrands', 'otherBrands', 'myBrands'].includes(brandFilter);

    useEffect(() => {
        if (isFaturado) {
            setFilter('Todos');
        }
    }, [isFaturado]);


    return (
        <>
            {ordersLoaded ? (
                <>
                    <Style.ComponentRoot>
                        <PanelTitle
                            ordersFilter={ordersFilter}
                            setOrdersFilter={setOrdersFilter}
                            brandFilter={brandFilter}
                            setBrandFilter={setBrandFilter}
                            hideOrdersFilter={hideOrdersFilter}
                            orders={orders}
                            filter={filter}
                            searchFilter={searchFilter}
                        />
                        {!isAtMaisCarrinho && (
                            <SearchBar
                                orders={orders}
                                filter={filter}
                                ordersFilter={ordersFilter}
                                searchFilter={searchFilter}
                                setSearchFilter={setSearchFilter}
                                brandFilter={brandFilter}
                            />
                        )}
                        <FilterButtons
                            orders={orders}
                            filter={filter}
                            setFilter={setFilter}
                            ordersFilter={ordersFilter}
                            setOrdersFilter={setOrdersFilter}
                            searchFilter={searchFilter}
                            brandFilter={brandFilter}
                        />

                        {!totalPanelCondition && !isAtMaisCarrinho && (
                            <PanelTotals
                                ordersFilter={ordersFilter}
                                filter={filter}
                                brandFilter={brandFilter}
                                totalFilterValue={totalFilterValue}
                            />
                        )}
                        <CardContent>
                            {!filteredOrders?.length ? (
                                <Style.ListContainer>
                                    <Style.ListItemContainer>
                                        {isAtMaisCarrinho ? (
                                            <>
                                                {!cartsLoaded ? (
                                                    <Grid
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'center',
                                                            marginTop: '1rem',
                                                        }}
                                                    >
                                                        <CircularProgress />
                                                    </Grid>
                                                ) : (
                                                    'Sem carrinhos'
                                                )}
                                            </>
                                        ) : (
                                            'Sem pedidos'
                                        )}
                                    </Style.ListItemContainer>
                                </Style.ListContainer>
                            ) : (
                                <>
                                    <PanelList
                                        filteredOrders={filteredOrders}
                                        multimarca={multimarca}
                                        setCurrentPage={setCurrentPage}
                                        cartsLoaded={cartsLoaded}
                                        loadClientData={loadClientData}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Style.ComponentRoot>
                </>
            ) : (
                <Grid
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '1rem',
                    }}
                >
                    <CircularProgress />
                </Grid>
            )}
        </>
    );
}

export default OrderListPanel;
